import { useState } from "react";
import {
  Download,
  Search,
  ChevronDown,
  Eye,
  CreditCard,
  Loader,
} from "lucide-react";
import { exportTableData } from "../../utils/exportTable";
import { fetchTransactions } from "../../services/transactionService";
import ViewTransactionModal from "../../modals/transactionModal/viewModal";
import Pagination from "../../components/Pagination";
import { useTransactions } from "../../hooks/useTransactions";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import formatCurrency from "../../utils/formatCurrency";

const Transactions = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  // --- Export Transactions Handler ---
  const handleExportTransactions = async () => {
    if (!pagination?.last_page) return;
    setExporting(true);
    try {
      const fetchPageData = async (page) => {
        const data = await fetchTransactions(page);
        return data?.message?.transactions?.data || [];
      };
      const columns = [
        { key: "tnx_ref", header: "Transaction Ref" },
        { key: "order_id", header: "Order" },
        { key: "user_id", header: "Customer" },
        { key: "amount", header: "Amount" },
        { key: "payment_method", header: "Method" },
        { key: "status", header: "Status" },
        { key: "created_at", header: "Date" },
      ];
      const mapRow = (t) => ({
        tnx_ref: t.tnx_ref,
        order_id: `ORD-${t.order_id}`,
        user_id: t.user_id,
        amount: t.amount,
        payment_method: t.payment_method,
        status: t.status,
        created_at: t.created_at,
      });
      const allRows = [];
      for (let page = 1; page <= pagination.last_page; page++) {
        // eslint-disable-next-line no-await-in-loop
        const pageData = await fetchPageData(page);
        allRows.push(...pageData.map(mapRow));
      }
      exportTableData(
        async () => allRows,
        1,
        columns,
        "transactions_export.csv",
      );
    } finally {
      setExporting(false);
    }
  };
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 400);
  const { transactions, transactionStats, pagination, loading, error } =
    useTransactions(currentPage, debouncedSearch, statusFilter);

  const stats = [
    {
      label: "Total Transactions",
      value: transactionStats.total_transactions ?? 0,
      color: "text-slate-900",
    },
    {
      label: "Total Revenue",
      value: formatCurrency(transactionStats.total_revenue),
      color: "text-slate-900",
    },
    {
      label: "Successful",
      value: transactionStats.succesful_transactions ?? 0,
      color: "text-green-600",
    },
    {
      label: "Failed",
      value: transactionStats.failed_transactions ?? 0,
      color: "text-rose-600",
    },
    {
      label: "Pending",
      value: transactionStats.pending_transactions ?? 0,
      color: "text-orange-500",
    },
  ];

  const getStatusStyles = (status) => {
    switch (status) {
      case "Success":
        return "bg-green-50 text-green-700 border-green-100";
      case "Pending":
        return "bg-orange-50 text-orange-600 border-orange-100";
      case "Failed":
        return "bg-rose-50 text-rose-600 border-rose-100";
      case "Refunded":
        return "bg-slate-50 text-slate-900 border-slate-200";
      default:
        return "bg-slate-50 text-slate-500";
    }
  };

  return (
    <div className="min-h-screen bg-white p-4 sm:p-6 md:p-8 font-sans text-slate-700">
      {/* --- HEADER --- */}
      <div className="flex flex-col sm:flex-row justify-between items-start mb-4 sm:mb-8 gap-3">
        <div>
          <h1 className="text-lg sm:text-xl font-medium text-[#0C0C0E]">
            Transactions
          </h1>
          <p className="font-medium text-xs sm:text-sm text-[#7D828D]">
            View and manage payment transactions
          </p>
        </div>
        <button
          className="flex items-center gap-2 bg-[#D2BDA1] text-black text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-lg font-medium hover:bg-[#c5b49f] transition-colors active:scale-95 active:shadow-sm duration-100 w-full sm:w-auto justify-center disabled:opacity-60"
          onClick={handleExportTransactions}
          disabled={loading || !pagination?.last_page || exporting}
        >
          {exporting ? (
            <>
              <Loader size={16} className="animate-spin" /> Exporting...
            </>
          ) : (
            <>
              <Download size={16} /> Export
            </>
          )}
        </button>
      </div>

      {/* --- STATS CARDS --- */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-0 mb-6 sm:mb-10 border-y border-[#EEF1F3]">
        {stats.map((stat, index) => (
          <div
            key={index}
            className={`p-4 sm:p-6 ${index !== stats.length - 1 ? "border-r border-[#EEF1F3] first:border-l-0 last:border-r-0" : ""}`}
          >
            <p className="text-[#7D828D] font-medium text-xs sm:text-sm mb-1 sm:mb-2">
              {stat.label}
            </p>
            <p className={`text-lg sm:text-2xl font-medium ${stat.color}`}>
              {stat.value}
            </p>
          </div>
        ))}
      </div>

      <h2 className="text-lg sm:text-xl font-medium mb-2 sm:mb-4 text-slate-900">
        Transactions
      </h2>

      {/* --- TOOLBAR --- */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div className="relative flex-1 max-w-full sm:max-w-sm">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            size={18}
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name..."
            className="w-full pl-10 pr-4 py-2 bg-white border border-[#F1F2F3] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D9CBBF] text-xs sm:text-sm"
          />
        </div>
        <div className="relative w-full sm:w-auto">
          <select
            className="appearance-none bg-[#FBF8F6] border border-[#F1F2F3] pl-4 pr-10 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D9CBBF] min-w-[120px] sm:min-w-[140px] font-medium text-slate-700 text-xs sm:text-sm w-full sm:w-auto"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </select>
          <ChevronDown
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            size={18}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader size={32} className="text-[#6B7143] animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 sm:px-6 py-4 rounded-lg text-xs sm:text-sm">
          {error}
        </div>
      ) : transactions.length > 0 ? (
        <>
          {/* --- TRANSACTIONS TABLE --- */}
          <div className="border border-[#EEF1F3] rounded-lg overflow-x-auto">
            <table className="min-w-[700px] w-full text-left border-collapse text-xs sm:text-sm">
              <thead>
                <tr className="bg-[#FBF8F6] border-b border-[#EEF1F3]">
                  <th className="px-4 sm:px-6 py-3 sm:py-4 font-medium text-[#7D828D]">
                    Transaction Ref
                  </th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4 font-medium text-[#7D828D]">
                    Order
                  </th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4 font-medium text-[#7D828D]">
                    Customer
                  </th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4 font-medium text-[#7D828D]">
                    Amount
                  </th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4 font-medium text-[#7D828D]">
                    Method
                  </th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4 font-medium text-[#7D828D] text-center">
                    Status
                  </th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4 font-medium text-[#7D828D]">
                    Date
                  </th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4 font-medium text-[#7D828D]">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EEF1F3]">
                {transactions.map((t, idx) => (
                  <tr
                    key={idx}
                    className="hover:bg-slate-50/50 transition-colors"
                  >
                    <td className="px-4 sm:px-6 py-3 sm:py-4 font-mono text-slate-500">
                      {t.tnx_ref}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 font-medium text-slate-900">
                      0RD-{t.order_id}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 font-medium text-slate-900">
                      {t.user?.name || "Unknown"}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 font-medium text-slate-900">
                      {formatCurrency(t.amount || 0)}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-4 bg-[#00C3F7] rounded-[2px]"></div>
                        <div className="flex flex-col leading-none">
                          <span className="font-bold text-slate-900 text-xs">
                            {t.payment_method || "N/A"}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4">
                      <div className="flex justify-center">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] sm:text-[11px] font-bold border ${getStatusStyles(t.status)}`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              t.status === "Success"
                                ? "bg-green-600"
                                : t.status === "Pending"
                                  ? "bg-orange-500"
                                  : t.status === "Failed"
                                    ? "bg-rose-600"
                                    : "bg-slate-900"
                            }`}
                          ></span>
                          {t.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4">
                      <div className="flex flex-col leading-tight">
                        {t.created_at ? (
                          <>
                            <span className="font-bold text-slate-900">
                              {new Date(t.created_at).toLocaleDateString(
                                "en-GB",
                                {
                                  year: "numeric",
                                  month: "2-digit",
                                  day: "2-digit",
                                }
                              )}
                            </span>
                            <span className="text-[10px] sm:text-[11px] text-slate-400">
                              {new Date(t.created_at).toLocaleTimeString(
                                "en-GB",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                }
                              )}
                            </span>
                          </>
                        ) : (
                          <span className="font-bold text-slate-900">N/A</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4">
                      <button
                        className="flex items-center gap-1.5 text-[#8B7E74] hover:text-slate-900 font-bold text-xs transition-colors active:scale-90 duration-100"
                        onClick={() => {
                          setSelectedTransaction(t);
                          setViewModalOpen(true);
                        }}
                      >
                        <Eye size={14} /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* --- PAGINATION --- */}
          {pagination && (
            <div className="p-4 sm:p-6 flex flex-col sm:flex-row gap-3 sm:gap-0 items-center justify-between border-t border-[#EEF1F3] bg-[#FBF8F6] w-full overflow-x-auto min-w-0">
              <span className="text-xs sm:text-sm text-[#7D828D]">
                Showing{" "}
                {pagination.current_page
                  ? (pagination.current_page - 1) * pagination.per_page + 1
                  : 0}{" "}
                to{" "}
                {Math.min(
                  pagination.current_page * pagination.per_page,
                  pagination.total,
                )}{" "}
                of {pagination.total} transactions
              </span>
              <div className="w-full sm:w-auto">
                <Pagination
                  currentPage={pagination.current_page}
                  totalPages={pagination.last_page}
                  onPageChange={setCurrentPage}
                />
              </div>
            </div>
          )}
        </>
      ) : (
        /* Empty State */
        <div className="flex flex-col items-center justify-center py-12 sm:py-16 px-4 sm:px-6 border border-[#EEF1F3] rounded-lg">
          <div className="p-1 border border-slate-300 bg-[#F8F5F1] rounded-full overflow-hidden mb-4">
            <CreditCard size={32} className="text-[#BFAC93]" />
          </div>
          <h4 className="font-medium text-sm sm:text-base text-[#0C0C0E] text-center mb-2">
            No payments yet
          </h4>
          <p className="text-xs sm:text-sm text-slate-400 text-center">
            Payment transactions will appear here once processed
          </p>
        </div>
      )}

      {/* View Transaction Modal */}
      <ViewTransactionModal
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        transaction={selectedTransaction}
      />
    </div>
  );
};

export default Transactions;
